import React from "react";
import { Theme } from "./types/profile";
import { colorOptions, fontOptions } from "./theme/themeConstants";

interface ProfileTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  theme: Theme | null;
}

const tabs = [
  { id: "profile", label: "My Profile", icon: "👤" },
  { id: "business", label: "Business Profile", icon: "💼" },
  { id: "theme", label: "Theme", icon: "🎨" },
  // { id: "configuration", label: "Page Configuration", icon: "⚙️" },
  // { id: "printerConfig", label: "Printer Config", icon: "🖨️" },
];

const ProfileTabs: React.FC<ProfileTabsProps> = ({ activeTab, onTabChange, theme }) => {
  const bgClass = theme?.background_color ? colorOptions[theme.background_color] : "bg-blue-600";
  const fontClass = theme?.font_family ? fontOptions[theme.font_family] : "font-sans";

  return (
    <div className='w-full'>
      {/* Mobile View - Horizontal Scroll */}
      <div className='lg:hidden'>
        <div className='flex space-x-1 pb-2 overflow-x-auto scrollbar-hide'>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`flex-shrink-0 px-3 py-2 rounded-md transition-all duration-200 ${fontClass} ${
                activeTab === tab.label
                  ? `${bgClass} text-white font-semibold shadow-md transform scale-105`
                  : `text-gray-600 hover:bg-gray-100 hover:text-gray-800 border border-gray-200`
              } whitespace-nowrap flex items-center space-x-1 min-w-max`}
              onClick={() => onTabChange(tab.label)}
            >
              <span className='text-sm'>{tab.icon}</span>
              <span className='text-sm font-medium'>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Desktop View - Vertical Stack */}
      <div className='hidden lg:flex lg:flex-col lg:gap-1.5'>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`px-4 py-2.5 text-left rounded-lg transition-all duration-200 ${fontClass} ${
              activeTab === tab.label
                ? `${bgClass} text-white font-semibold shadow-md border-l-4 border-white border-opacity-50 transform -translate-x-[2px]`
                : `hover:bg-gradient-to-r hover:from-gray-50 hover:to-white hover:shadow-sm border-l-4 border-transparent hover:border-gray-200`
            } group`}
            onClick={() => onTabChange(tab.label)}
          >
            <div className='flex items-center space-x-2'>
              <span className='text-base transition-transform duration-200 group-hover:scale-110'>{tab.icon}</span>
              <div className='flex flex-col leading-tight'>
                <span className='font-medium text-sm'>{tab.label}</span>
                <span className={`text-xs ${activeTab === tab.label ? "text-white text-opacity-90" : "text-gray-500"}`}>
                  {getTabDescription(tab.id)}
                </span>
              </div>
              {activeTab === tab.label && <span className='ml-auto text-white text-opacity-80 text-sm'>▶</span>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// Helper function to get tab descriptions
const getTabDescription = (tabId: string): string => {
  const descriptions: Record<string, string> = {
    profile: "Manage personal info",
    business: "Company details",
    theme: "Customize theme",
    security: "Privacy & password",
    import: "Import data",
    export: "Export data",
  };
  return descriptions[tabId] || "Manage settings";
};

export default ProfileTabs;
